import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Lottie from "../../../../../components/animation/LottieAnimation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { displayBox } from "../../BookingStyle";
import {
  GOOGLE_PLACES_MIN_QUERY_LENGTH,
  getGooglePlaceAddress,
  getGooglePredictions,
  isGooglePlacesOption,
  normalizeSearchText,
} from "../helpers/googlePlacesAutocompleteHelpers";
import { normalizeAddress } from "../helpers/addressRouteHelpers";

const today = dayjs().startOf("day");

const BookingFormFields = ({
  values,
  errors,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  t,
  riderOption,
  riderOptions,
  handleRiderOptionChange,
  handleOpenOtherPersonModal,
  otherPersonPreview,
  addressApi,
  addressOrigin,
  setAddressOrigin,
  addressDestination,
  addressDestinationText,
  destinationInputValue,
  setDestinationInputValue,
  openDestinationSelect,
  setOpenDestinationSelect,
  handleOpenSubRoutesModal,
  handleDestinationChange,
  handleOpenOriginMapModal,
  handleOpenDestinationMapModal,
  getAddressLabel,
  model,
  bookingTypes,
  date,
  setDate,
  time,
  setTime,
  dateEnd,
  setDateEnd,
  timeEnd,
  setTimeEnd,
  priceTotal,
  formatPrice,
  priceSummarySx,
  isBookingActive,
  isLoading,
  setOpenOffers,
  bookingCount,
  loaderClassName,
  invalidField,
  invalidFields,
  clearInvalidField,
  showPrice,
}) => {
  const [originInputValue, setOriginInputValue] = useState("");
  const [googleOriginOptions, setGoogleOriginOptions] = useState([]);
  const [googleDestinationOptions, setGoogleDestinationOptions] = useState([]);

  useEffect(() => {
    const nextOriginInputValue = addressOrigin
      ? getAddressLabel(addressOrigin) || addressOrigin.name || ""
      : "";

    setOriginInputValue(nextOriginInputValue);

    if (!addressOrigin) {
      setGoogleOriginOptions([]);
    }
  }, [addressOrigin, getAddressLabel]);

  const originList = useMemo(() => addressApi || [], [addressApi]);
  const originSearchValue = normalizeSearchText(originInputValue);
  const originListOptions = useMemo(() => {
    if (!originSearchValue) return originList;

    return originList.filter((option) =>
      normalizeSearchText(getAddressLabel(option) || option?.name).includes(
        originSearchValue,
      ),
    );
  }, [getAddressLabel, originList, originSearchValue]);
  const shouldSearchGoogleOrigin =
    originSearchValue.length >= GOOGLE_PLACES_MIN_QUERY_LENGTH &&
    originListOptions.length === 0;
  const originOptions = shouldSearchGoogleOrigin
    ? googleOriginOptions
    : originListOptions;
  const destinationSearchValue = normalizeSearchText(destinationInputValue);
  const destinationListOptions = useMemo(() => {
    const availableOptions = originList;
    if (!destinationSearchValue) return availableOptions;

    return availableOptions.filter((option) =>
      normalizeSearchText(getAddressLabel(option) || option?.name).includes(
        destinationSearchValue,
      ),
    );
  }, [destinationSearchValue, getAddressLabel, originList]);
  const shouldSearchGoogleDestination =
    !addressDestination &&
    destinationSearchValue.length >= GOOGLE_PLACES_MIN_QUERY_LENGTH &&
    destinationListOptions.length === 0;
  const destinationOptions = shouldSearchGoogleDestination
    ? googleDestinationOptions
    : destinationListOptions;

  useEffect(() => {
    if (!shouldSearchGoogleOrigin) {
      setGoogleOriginOptions([]);
      return undefined;
    }

    let isMounted = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        const predictions = await getGooglePredictions(originInputValue);
        if (isMounted) {
          setGoogleOriginOptions(predictions);
        }
      } catch (error) {
        console.log(error);
        if (isMounted) {
          setGoogleOriginOptions([]);
        }
      }
    }, 350);

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [originInputValue, shouldSearchGoogleOrigin]);

  useEffect(() => {
    if (!shouldSearchGoogleDestination) {
      setGoogleDestinationOptions([]);
      return undefined;
    }

    let isMounted = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        const predictions = await getGooglePredictions(destinationInputValue);
        if (isMounted) {
          setGoogleDestinationOptions(predictions);
        }
      } catch (error) {
        console.log(error);
        if (isMounted) {
          setGoogleDestinationOptions([]);
        }
      }
    }, 350);

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [destinationInputValue, shouldSearchGoogleDestination]);

  const handleGoogleOriginChange = async (newValue) => {
    try {
      const googleAddress = await getGooglePlaceAddress(newValue);
      setAddressOrigin(normalizeAddress(googleAddress));
      setOriginInputValue(googleAddress.name);
      setGoogleOriginOptions([]);
    } catch (error) {
      console.log(error);
    }
  };

  const handleGoogleDestinationChange = async (newValue) => {
    try {
      const googleAddress = await getGooglePlaceAddress(newValue);
      handleDestinationChange(googleAddress);
      setGoogleDestinationOptions([]);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOriginChange = async (event, newValue) => {
    clearInvalidField(invalidFields.origin);
    if (!newValue) {
      setAddressOrigin(null);
      setOriginInputValue("");
      return;
    }
    if (isGooglePlacesOption(newValue)) {
      await handleGoogleOriginChange(newValue);
      return;
    }
    setAddressOrigin(normalizeAddress(newValue));
    setOriginInputValue(getOriginOptionLabel(newValue));
  };

  const getOriginOptionLabel = (option) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    if (isGooglePlacesOption(option)) return option.description || option.name;
    return getAddressLabel(option) || option.name || "";
  };

  const handleDestinationAutocompleteChange = async (event, newValue) => {
    clearInvalidField(invalidFields.destination);
    if (isGooglePlacesOption(newValue)) {
      await handleGoogleDestinationChange(newValue);
      return;
    }
    handleDestinationChange(newValue);
  };

  return (
    <Grid container spacing={1} sx={{ alignItems: "center" }}>
      <Grid item xs={12} sm={riderOption === riderOptions.otherPerson ? 6 : 12}>
        <FormControl
          fullWidth
          error={
            invalidField === invalidFields.riderOption ||
            (touched.riderOption && Boolean(errors.riderOption))
          }
        >
          <InputLabel>Para quien es el Servicio</InputLabel>
          <Select
            value={riderOption}
            id="riderOption"
            name="riderOption"
            label="Para quien es el Servicio"
            onBlur={handleBlur}
            onChange={(event) => {
              const selectedRiderOption = Number(event.target.value);
              handleRiderOptionChange(event);
              setFieldValue("riderOption", selectedRiderOption);
              if (selectedRiderOption === riderOptions.otherPerson) {
                handleOpenOtherPersonModal();
              }
            }}
          >
            <MenuItem value={0} disabled>
              Seleccione una opcion
            </MenuItem>
            <MenuItem value={riderOptions.me}>Para Mi</MenuItem>
            <MenuItem value={riderOptions.otherPerson}>
              Para Otra persona
            </MenuItem>
          </Select>
          {touched.riderOption && errors.riderOption && (
            <FormHelperText>{errors.riderOption}</FormHelperText>
          )}
        </FormControl>
      </Grid>

      {riderOption === riderOptions.otherPerson && (
        <Grid item xs={12} sm={6}>
          <Box
            role="button"
            tabIndex={0}
            onClick={handleOpenOtherPersonModal}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleOpenOtherPersonModal();
              }
            }}
            sx={{
              border: "1px solid",
              borderColor:
                invalidField === invalidFields.otherPersonName ||
                invalidField === invalidFields.otherPersonPhone
                  ? "error.main"
                  : "divider",
              borderRadius: "4px",
              minHeight: 56,
              px: 1,
              display: "flex",
              textAlign: "center",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              "&:hover": {
                borderColor: "primary.main",
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {otherPersonPreview.name || "NOMBRE"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {otherPersonPreview.phone || "TELEFONO"}
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        {addressApi ? (
          <Autocomplete
            value={addressOrigin}
            inputValue={originInputValue}
            disablePortal
            name="addressOrigin"
            id="combo-box-origin"
            onChange={handleOriginChange}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "reset") return;
              setOriginInputValue(newInputValue);
            }}
            filterOptions={(options) => options}
            options={originOptions}
            getOptionLabel={getOriginOptionLabel}
            isOptionEqualToValue={(option, value) =>
              getOriginOptionLabel(option) === getOriginOptionLabel(value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("origin")}
                error={invalidField === invalidFields.origin}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Abrir mapa de origen"
                          onMouseDown={(event) => {
                            event.preventDefault();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleOpenOriginMapModal();
                          }}
                        >
                          <LocationOnIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : (
          <TextField
            fullWidth
            disabled
            type="text"
            label={t("Lista de Direcciones")}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        {addressApi ? (
          <Autocomplete
            value={addressDestination}
            inputValue={
              addressDestination
                ? addressDestinationText
                : destinationInputValue
            }
            onInputChange={(event, newInputValue) => {
              if (addressDestination) return;
              setDestinationInputValue(newInputValue);
            }}
            open={openDestinationSelect}
            onOpen={() => {
              if (addressDestination) {
                handleOpenSubRoutesModal();
                return;
              }
              setOpenDestinationSelect(true);
            }}
            onClose={() => {
              setOpenDestinationSelect(false);
            }}
            disablePortal
            name="addressDestination"
            id="combo-box-destination"
            onChange={handleDestinationAutocompleteChange}
            filterOptions={(options) => options}
            options={destinationOptions}
            getOptionLabel={getOriginOptionLabel}
            isOptionEqualToValue={(option, value) =>
              getOriginOptionLabel(option) === getOriginOptionLabel(value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("destination")}
                error={invalidField === invalidFields.destination}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {addressDestination && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Agregar subruta"
                            sx={{
                              padding: 0,
                              color: "success.main",
                              transition:
                                "background-color 0.2s ease, color 0.2s ease",
                              "&:hover": {
                                bgcolor: "primary.main",
                                color: "common.white",
                              },
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              handleOpenSubRoutesModal();
                            }}
                          >
                            <Lottie
                              src="/assets/routes.json"
                              loop
                              autoplay
                              style={{
                                width: 35,
                                height: 35,
                                pointerEvents: "none",
                              }}
                            />
                          </IconButton>
                        </InputAdornment>
                      )}
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Abrir mapa de destino"
                          onMouseDown={(event) => {
                            event.preventDefault();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleOpenDestinationMapModal();
                          }}
                        >
                          <LocationOnIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                onClick={(event) => {
                  if (!addressDestination) return;
                  event.preventDefault();
                  handleOpenSubRoutesModal();
                }}
              />
            )}
          />
        ) : (
          <TextField
            fullWidth
            disabled
            type="text"
            label={t("Lista de Direcciones")}
          />
        )}
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          value={values.persons}
          fullWidth
          type="number"
          label={t("persons")}
          onBlur={handleBlur}
          onChange={(event) => {
            clearInvalidField(invalidFields.persons);
            handleChange(event);
          }}
          name="persons"
          error={
            invalidField === invalidFields.persons ||
            (!!touched.persons && !!errors.persons)
          }
          helperText={touched.persons && errors.persons}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth error={invalidField === invalidFields.vehicle}>
          <InputLabel>{t("vehicle")}</InputLabel>
          <Select
            value={values.vehicle}
            id="vehicle"
            name="vehicle"
            label={t("vehicle")}
            onBlur={handleBlur}
            onChange={(event) => {
              clearInvalidField(invalidFields.vehicle);
              handleChange(event);
            }}
            error={
              invalidField === invalidFields.vehicle ||
              (!!touched.vehicle && !!errors.vehicle)
            }
          >
            {model.map((vehicleModel, index) => (
              <MenuItem key={index} value={vehicleModel.id}>
                {t(vehicleModel.name)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid
        item
        xs={12}
        sm={6}
        md={values.booking === bookingTypes.perHours ? 4 : 6}
      >
        <FormControl fullWidth error={invalidField === invalidFields.booking}>
          <InputLabel>{t("Solicitar")}</InputLabel>
          <Select
            id="booking"
            value={values.booking}
            name="booking"
            label="Solicitar"
            onChange={(event) => {
              clearInvalidField(invalidFields.booking);
              handleChange(event);
            }}
            error={invalidField === invalidFields.booking}
          >
            <MenuItem value={0} disabled>
              Seleccione una opcion
            </MenuItem>
            <MenuItem value={bookingTypes.now}>{t("now")}</MenuItem>
            <MenuItem value={bookingTypes.later}>
              {t("later")}(reservar)
            </MenuItem>
            <MenuItem value={bookingTypes.perHours}>{t("perHours")}</MenuItem>
            <MenuItem value={bookingTypes.quote}>Cotizar</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {Boolean(values.booking) && values.booking !== bookingTypes.now && (
        <Grid item xs={6} sm={values.booking === bookingTypes.perHours ? 2 : 3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2}>
              <MobileDatePicker
                label={
                  values.booking === bookingTypes.perHours
                    ? t("startDate")
                    : t("date")
                }
                inputFormat="DD/MM/YYYY"
                name="date"
                value={date}
                minDate={today}
                onChange={(newValue) => {
                  clearInvalidField(invalidFields.date);
                  setDate(newValue);
                  setDateEnd(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={params.error || invalidField === invalidFields.date}
                  />
                )}
              />
            </Stack>
          </LocalizationProvider>
        </Grid>
      )}

      {Boolean(values.booking) && values.booking !== bookingTypes.now && (
        <Grid item xs={6} sm={values.booking === bookingTypes.perHours ? 2 : 3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={3}>
              <MobileTimePicker
                label={
                  values.booking === bookingTypes.perHours
                    ? t("startTime")
                    : t("time")
                }
                value={time}
                name="time"
                onChange={(newValue) => {
                  clearInvalidField(invalidFields.time);
                  setTime(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={params.error || invalidField === invalidFields.time}
                  />
                )}
              />
            </Stack>
          </LocalizationProvider>
        </Grid>
      )}

      <Grid
        item
        xs={6}
        sm={2}
        sx={displayBox(
          values.booking,
          bookingTypes.perHours,
          bookingTypes.perHours,
        )}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <MobileDatePicker
              label={t("endDate")}
              inputFormat="DD/MM/YYYY"
              name="dateEnd"
              value={dateEnd}
              minDate={date || today}
              onChange={(newValue) => {
                clearInvalidField(invalidFields.dateEnd);
                setDateEnd(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={params.error || invalidField === invalidFields.dateEnd}
                />
              )}
            />
          </Stack>
        </LocalizationProvider>
      </Grid>

      <Grid
        item
        xs={6}
        sm={2}
        sx={{
          display: {
            xs: values.booking === bookingTypes.perHours ? "block" : "none",
            md: values.booking === bookingTypes.perHours ? "block" : "none",
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={3}>
            <MobileTimePicker
              label={t("endTime")}
              value={timeEnd}
              name="timeEnd"
              onChange={(newValue) => {
                clearInvalidField(invalidFields.timeEnd);
                setTimeEnd(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={params.error || invalidField === invalidFields.timeEnd}
                />
              )}
            />
          </Stack>
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12}>
        <TextField
          value={values.note}
          fullWidth
          type="text"
          label={t("notes")}
          onBlur={handleBlur}
          onChange={handleChange}
          name="note"
          error={!!touched.note && !!errors.note}
          helperText={touched.note && errors.note}
          multiline
          inputProps={{
            maxLength: 200,
            maxRows: 4,
            style: { textTransform: "uppercase" },
          }}
        />
      </Grid>

      {showPrice && (
        <Grid item xs={12} sm={2}>
          <Box sx={priceSummarySx}>{formatPrice(priceTotal)}</Box>
        </Grid>
      )}

      <Grid
        item
        xs={isBookingActive ? 8 : 12}
        sm={showPrice ? (isBookingActive ? 7 : 10) : isBookingActive ? 9 : 12}
      >
        <Button
          fullWidth
          type="submit"
          color="primary"
          variant="contained"
          sx={{ height: 45 }}
          disabled={isLoading}
        >
          <Typography variant="string">
            {values.booking === bookingTypes.now
              ? t("booknow")
              : t("booklater")}
          </Typography>
        </Button>
      </Grid>

      {isBookingActive && (
        <Grid item xs={4} sm={3}>
          <Button
            fullWidth
            type="button"
            color="primary"
            variant="contained"
            sx={{
              height: 45,
              display: "flex",
              padding: 0,
            }}
            disabled={isLoading}
            onClick={(event) => {
              event.preventDefault();
              setOpenOffers(true);
            }}
          >
            Pedidos ({bookingCount})
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

BookingFormFields.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  riderOption: PropTypes.number.isRequired,
  riderOptions: PropTypes.shape({
    me: PropTypes.number.isRequired,
    otherPerson: PropTypes.number.isRequired,
  }).isRequired,
  handleRiderOptionChange: PropTypes.func.isRequired,
  handleOpenOtherPersonModal: PropTypes.func.isRequired,
  otherPersonPreview: PropTypes.shape({
    name: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  addressApi: PropTypes.array.isRequired,
  addressOrigin: PropTypes.object,
  setAddressOrigin: PropTypes.func.isRequired,
  addressDestination: PropTypes.object,
  addressDestinationText: PropTypes.string.isRequired,
  destinationInputValue: PropTypes.string.isRequired,
  setDestinationInputValue: PropTypes.func.isRequired,
  openDestinationSelect: PropTypes.bool.isRequired,
  setOpenDestinationSelect: PropTypes.func.isRequired,
  handleOpenSubRoutesModal: PropTypes.func.isRequired,
  handleDestinationChange: PropTypes.func.isRequired,
  handleOpenOriginMapModal: PropTypes.func.isRequired,
  handleOpenDestinationMapModal: PropTypes.func.isRequired,
  getAddressLabel: PropTypes.func.isRequired,
  model: PropTypes.array.isRequired,
  bookingTypes: PropTypes.shape({
    now: PropTypes.number.isRequired,
    later: PropTypes.number.isRequired,
    perHours: PropTypes.number.isRequired,
    quote: PropTypes.number.isRequired,
  }).isRequired,
  date: PropTypes.object,
  setDate: PropTypes.func.isRequired,
  time: PropTypes.object,
  setTime: PropTypes.func.isRequired,
  dateEnd: PropTypes.object,
  setDateEnd: PropTypes.func.isRequired,
  timeEnd: PropTypes.object,
  setTimeEnd: PropTypes.func.isRequired,
  priceTotal: PropTypes.number.isRequired,
  formatPrice: PropTypes.func.isRequired,
  priceSummarySx: PropTypes.object.isRequired,
  isBookingActive: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setOpenOffers: PropTypes.func.isRequired,
  bookingCount: PropTypes.number.isRequired,
  loaderClassName: PropTypes.string.isRequired,
  invalidField: PropTypes.string.isRequired,
  invalidFields: PropTypes.object.isRequired,
  clearInvalidField: PropTypes.func.isRequired,
  showPrice: PropTypes.bool.isRequired,
};

export default BookingFormFields;
